/**
 * myPokemonTypes: THE typing of a My Pokémon record, from the trusted table.
 *
 * Owner direction (2026-09-20): anything the app can take from a real,
 * trustworthy list it already holds (a species' types above all) honours
 * that list, never a scan. A scan reads pixels through an overlay and can
 * come back short: an appraisal frame read one of Medicham's two pills, the
 * record kept "Fighting" alone, and the Showcases page lost Medicham from
 * the Psychic row. So every surface that names a record's types goes through
 * this one accessor, and the store writes the same answer into record.types.
 *
 * Rules:
 * - No species: the stored list, filtered to real types (the only source).
 * - Base form (formId null) or a costume / non-regional form: the species
 *   table (data/speciesTypes), which is complete for #1..#1025.
 * - A REGIONAL form (rattata-alola, slowbro-galar): the app holds no
 *   per-form typing table yet, so a full two-pill read of the form's own
 *   page is kept when it exists; a single stored pill or nothing falls back
 *   to the base species' table. TODO(regional-types): a curated regional
 *   typing table would retire the stored list entirely.
 *
 * Pure.
 */
import { isPokeType, typesFor, type PokeType } from "./types";

/** The slug rule formDexRoster uses: the region is a whole segment of the id. */
const REGION_SEGMENT = /(^|-)(alola|galar|hisui|paldea)(-|$)/;
const REGIONAL_INCLUDE = new Set(["basculin-white-striped"]);
const REGIONAL_EXCLUDE = new Set(["pikachu-alola-cap"]);

/** Whether a record's form id names a regional form (its typing may differ from the base species'). */
export function isRegionalFormId(formId: string | null | undefined): boolean {
  if (!formId) return false;
  if (REGIONAL_EXCLUDE.has(formId)) return false;
  if (REGIONAL_INCLUDE.has(formId)) return true;
  return REGION_SEGMENT.test(formId);
}

export interface MyPokemonTypeSource {
  speciesId: number | null;
  formId?: string | null;
  types?: readonly string[] | null;
}

export function myPokemonTypes(r: MyPokemonTypeSource): PokeType[] {
  const stored = (r.types ?? []).map((t) => t.toLowerCase()).filter(isPokeType);
  if (r.speciesId == null) return stored;
  const base = typesFor(r.speciesId);
  if (!isRegionalFormId(r.formId)) return base;
  // A regional form: a full read (two pills, or one pill that is not simply
  // the base's first type seen alone) is the only typing the app has for it.
  if (stored.length >= 2) return stored;
  if (stored.length === 1 && !base.includes(stored[0])) return stored;
  return base;
}
